import React from 'react';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import NumberInput from './NumberInput/NumberInput';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './MinMaxInputs.scss';

interface MinMaxInputsProps {
	label?: string,
	id: string,
	wrapperClass?: string,
	callback: (values: number[]) => void,
	minLabel?: string,
	maxLabel?: string,
	minValue: string,
	globalMinValue: number,
	setMinValue: React.Dispatch<React.SetStateAction<string>>,
	maxValue: string,
	globalMaxValue: number,
	setMaxValue: React.Dispatch<React.SetStateAction<string>>,
	minPossibleValue?: number,
	maxPossibleValue?: number,
	currency?: boolean,
	commaSeparated?: boolean,
}

const MinMaxInputs = ({
	label,
	id,
	minValue,
	maxValue,
	globalMaxValue,
	setMinValue,
	minLabel,
	globalMinValue,
	maxLabel,
	setMaxValue,
	wrapperClass,
	callback,
	minPossibleValue,
	maxPossibleValue,
	currency,
	commaSeparated
}: MinMaxInputsProps
) => {
	const minValueNumber = parseInt(minValue.replace(/,/g, ''));
	const maxValueNumber = parseInt(maxValue.replace(/,/g, ''));

	const validateAndCallback = () => {

		const OutOfRange = (value: number) => {
			if (minPossibleValue && value < minPossibleValue)
				return true;
			if (maxPossibleValue && value > maxPossibleValue)
				return true;
			return false;
		};

		if (OutOfRange(minValueNumber)) {
			setMinValue(globalMinValue === 0 ? '' : `${globalMinValue}`);															// Reset Min value in UI to current AppState Min
			toast.warn('Min is out of range.', {
				icon: <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />,
				toastId: 'minOutOfRange',											// assign unique id, preventing toast from showing more than one at a time
				containerId: 'filterWarningToastContainer'  // set containerId so toast displays in specific toast container
			});
			return;
		}
		if (OutOfRange(maxValueNumber)) {
			toast.warn('Max is out of range.', {
				icon: <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />,
				toastId: 'maxOutOfRange',											// assign unique id, preventing toast from showing more than one at a time
				containerId: 'filterWarningToastContainer'  // set containerId so toast displays in specific toast container
			});
			setMaxValue(globalMaxValue === -1 ? '' : `${globalMaxValue}`);															// Reset Max value in UI to current AppState Max
			return;
		}

		if (minValue && (!maxValue || isNaN(maxValueNumber)))
			callback([minValueNumber, -1]);

		if (maxValue && (!minValue || isNaN(minValueNumber))) {  	// IF user entered Max but no Min
			if (maxValue !== '0') {
				callback([0, maxValueNumber]);
			} else {
				setMaxValue(globalMaxValue === -1 ? '' : `${globalMaxValue}`);	// Reset Max input value to AppState priceMax
				toast.warn('Max cannot be 0.', {
					icon: <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />,
					toastId: 'maxNotZero',											// assign unique id, preventing toast from showing more than one at a time
					containerId: 'filterWarningToastContainer'  // set containerId so toast displays in specific toast container
				});
			}
		}

		if ((minValue && maxValue) &&
			(!isNaN(minValueNumber) && !isNaN(maxValueNumber))
		) {
			if (minValueNumber <= maxValueNumber) {
				callback([minValueNumber, maxValueNumber]);
			} else {
				if (globalMinValue !== 0 && globalMaxValue === -1) {
					setMaxValue('');

					if (maxValue === '0') {
						toast.warn('Max cannot be 0.', {
							icon: <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />,
							toastId: 'maxNotZero',
							containerId: 'filterWarningToastContainer'
						});
					} else {
						toast.warn('Max must be greater than Min.', {
							icon: <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />,
							toastId: 'maxGreaterThanMin',  // assign same toastId as toast above w/ same msg (prevents duplicates)
							containerId: 'filterWarningToastContainer'
						});
					}
				} else if (globalMinValue === 0 && globalMaxValue !== -1) { // OTHERWISE IF AppState Max has a value but not AppState Min
					setMinValue('');																								// Clear out Min value in UI	
					toast.warn('Min must be less than Max.', {											// show toast
						icon: <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />,
						toastId: 'minLessThanMax',
						containerId: 'filterWarningToastContainer'
					});
				} else {																												// OTHERWISE IF no cases aove are met
					setMaxValue(`${globalMaxValue}`);															// Reset Max value in UI to current AppState Max
					setMinValue(`${globalMinValue}`);															// Reset Min value in UI to current AppState Min

					if (maxValue === '0') {  																			// IF user enters '0' for Max	                                
						toast.warn('Max cannot be 0.', {										 					// show toast
							icon: <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />,
							toastId: 'maxNotZero',
							containerId: 'filterWarningToastContainer'
						});
					} else {																											// OTHERWISE (IF Max value !== '0')
						toast.warn('Max must be greater than Min.', {										// show toast with generic messaging re: Min <= Max
							icon: <FontAwesomeIcon icon={['fas', 'exclamation-triangle']} />,
							toastId: 'maxGreaterThanMin',																	// assign same toastId as toast above w/ same msg (prevents duplicates)
							containerId: 'filterWarningToastContainer'
						});
					}
				}
			}
		}

		if (
			(minValue === '' && maxValue === '') ||						// IF UI Min/Max inputs are both empty OR non-numbers
			(isNaN(minValueNumber) && isNaN(maxValueNumber))
		) {
			if (globalMinValue === 0 && globalMaxValue === -1) return;  // return IF AppState Min/Max are default values
			callback([0, -1]);
		}

	};

	return (
		<div id={id} className={classNames(wrapperClass && wrapperClass, 'pb-2')}>
			{label && (
				<div className="cc-min-max-inputs__label cc-font-weight--normal mb-0">{label}</div>
			)}
			<div className='cc-min-max-inputs w-100'>
				<NumberInput
					currency={currency}
					commaSeparated={commaSeparated}
					id={`${id}-min`}
					label={minLabel !== undefined ? minLabel : 'Min'}
					value={minValue}
					className="form-control"
					placeholder="No Min"
					onChange={setMinValue}
					onBlur={() => validateAndCallback()}
				/>

				<div className={`${minLabel === '' && maxLabel === '' ? 'mt-3' : 'cc-min-max-inputs__spacer'} px-3`}>to</div>

				<NumberInput
					currency={currency}
					commaSeparated={commaSeparated}
					id={`${id}-max`}
					label={maxLabel !== undefined ? maxLabel : 'Max'}
					value={maxValue}
					className="form-control"
					placeholder="No Max"
					onChange={setMaxValue}
					onBlur={() => validateAndCallback()}
				/>
			</div>
		</div>
	);
};

export default MinMaxInputs;